import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import { FaEye } from "react-icons/fa";
import { MdDelete, MdEdit } from "react-icons/md";

import {
    formatDateTime,
    convertURLParams,
    parseSearchParams,
} from "@/utils/helpers";

import Modal from "@/components/Modal";
import ConfirmDelete from "@/components/ConfirmDelete";

import Table from "../../components/Table/Table";
import HeaderContent from "../../components/Table/components/HeaderContent";
import FooterContent from "../../components/Table/components/FooterContent";

import { useLectureQuizQuestions } from "../hooks/useLectureQuizQuestions";
import QuestionView from "./QuestionView";
import usePermission from "../../../../hooks/usePermission";
import { useDeleteQuizQuestion } from "../hooks/useDeleteQuizQuestion";
import CreateQuestion from "./CreateQuestion";
import UpdateQuestion from "./UpdateQuestion";

function QuizTable() {
    const { can, canAll } = usePermission();

    const { search } = useLocation();
    const [searchValue] = useState("");
    const [perPage, setPerPage] = useState(10);
    const [searchParams] = useSearchParams();

    const { courseId, lectureId, itemId } = useParams();

    const { deleteQuizQuestion, isDeleting } = useDeleteQuizQuestion();

    //include
    const includeFields = can("read:course:lecture:quiz:quiestion:options") && [
        "options",
    ];
    const page = parseSearchParams(
        searchParams,
        "page",
        (value) => (parseInt(value, 10) < 1 ? 1 : parseInt(value, 10)),
        1
    );

    // filter , sort , page
    const filterAndSortAndPageQuery = convertURLParams(search);

    const { quizQuestions, isPending } = useLectureQuizQuestions({
        courseId,
        lectureId,
        itemId,
        searchValue,
        includeFields,
        perPage,
        page,
        filterAndSortAndPageQuery,
    });

    //headerRows
    const headers = [
        { uid: "lectureItemId", name: "كود الامتحان", sortable: false },
        { uid: "id", name: "#", sortable: true },
        { uid: "question", name: "السؤال", sortable: false },
        { uid: "createdAt", name: "أنشئت في", sortable: true },
        { uid: "actions", name: "الافعال", sortable: false },
    ];

    //defaultHeaders
    const INITIAL_VISIBLE_COLUMNS = ["id", "question", "actions"];

    //Actions
    const actions = useMemo(
        () => [
            {
                id: "rowDetails",
                name: "رؤية",
                icon: <FaEye className="text-lg" />,
                content: (row) => <QuestionView question={row} />,
                permissions: ["read:course:lecture:quiz:quiestions"],
            },
            {
                id: "edit",
                name: "تعديل",
                icon: <MdEdit className="text-lg text-blue-color-primary" />,
                content: (row) => <UpdateQuestion row={row} />,
                permissions: ["update:course:lecture:quiz:quiestions"],
            },
            {
                id: "delete",
                name: "حذف",
                icon: <MdDelete className="text-lg text-red-color-primary" />,
                permissions: ["delete:course:lecture:quiz:quiestions"],
            },
        ],
        []
    );

    //addRow
    const addRow = {
        row: <CreateQuestion />,
        permission: can("create:course:lecture:quiz:quiestions"),
    };
    // formatting Data
    const reformattedData = quizQuestions?.data?.map((item) => {
        const { id, attributes, relationships, ...rest } = item;

        const quizQuestionsData = {
            id,
            ...attributes,
            lectureId: attributes?.lectureItemId,
            options: relationships?.options,
        };

        return {
            ...quizQuestionsData,
            ...rest,
        };
    });

    const renderCell = useCallback(
        (row, columnKey) => {
            const cellValue = row?.[columnKey];
            switch (columnKey) {
                case "createdAt":
                    return (
                        <span className="flex flex-col gap-2">
                            <span>
                                {formatDateTime(cellValue).formattedTime}
                            </span>
                            <span>
                                {formatDateTime(cellValue).formattedDate}
                            </span>
                        </span>
                    );
                case "updatedAt":
                    return (
                        <span className="flex flex-col gap-2">
                            <span>
                                {formatDateTime(cellValue).formattedTime}
                            </span>
                            <span>
                                {formatDateTime(cellValue).formattedDate}
                            </span>
                        </span>
                    );
                case "actions":
                    return (
                        <div className="relative flex items-center justify-start gap-2">
                            <Modal>
                                {actions?.map(
                                    (action) =>
                                        canAll(action.permissions) && (
                                            <React.Fragment key={action.id}>
                                                <Modal.Open opens={action.id}>
                                                    <button>
                                                        {action.icon}
                                                    </button>
                                                </Modal.Open>
                                                <Modal.Window name={action.id}>
                                                    {action.id ===
                                                    "rowDetails" ? (
                                                        action.content(row)
                                                    ) : action.id === "edit" ? (
                                                        action.content(row)
                                                    ) : action.id ===
                                                      "delete" ? (
                                                        <ConfirmDelete
                                                            resourceName={
                                                                row?.question
                                                            }
                                                            disabled={
                                                                isDeleting
                                                            }
                                                            onConfirm={() =>
                                                                deleteQuizQuestion(
                                                                    {
                                                                        courseId,
                                                                        lectureId,
                                                                        itemId,
                                                                        questionId:
                                                                            row?.id,
                                                                    }
                                                                )
                                                            }
                                                        />
                                                    ) : null}
                                                </Modal.Window>
                                            </React.Fragment>
                                        )
                                )}
                            </Modal>
                        </div>
                    );
                default:
                    return cellValue;
            }
        },
        [
            actions,
            courseId,
            deleteQuizQuestion,
            isDeleting,
            itemId,
            lectureId,
            canAll,
        ]
    );

    const [visibleColumns, setVisibleColumns] = useState(
        new Set(INITIAL_VISIBLE_COLUMNS)
    );

    const totalPages = Math.ceil(
        quizQuestions?.meta?.total / quizQuestions?.meta?.per_page
    );

    return (
        <>
            <Table
                isloading={isPending}
                rows={reformattedData}
                headers={headers}
                visibleColumns={visibleColumns}
                renderCell={renderCell}
                topContent={
                    <HeaderContent
                        // filterOptions={filterOptions}
                        visibleColumns={visibleColumns}
                        setVisibleColumns={setVisibleColumns}
                        headers={headers}
                        rowsNumber={perPage}
                        setRowsNumber={setPerPage}
                        addRow={addRow}
                        totalRows={quizQuestions?.meta?.total}
                        modalWidth="w-full md:w-[90%] lg:w-1/2"
                    />
                }
                bottomContent={<FooterContent totalPages={totalPages} />}
            />
        </>
    );
}
export default QuizTable;
